import { filterCandidates } from "../../utils/searchResults";
import medLogo from "../../assets/customers/med_institute/med-institute-logo.png";

const CustomerReport = ({ searchResults, setShowCustomerReport }) => {
    
    const handlePrint = () => {
        window.print();
    };

    const handleEmail = () => {
        const subject = "Predicate Device Comparison Report";
        const body = "Please find the attached predicate device comparison report.";
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const predicateCandidates = filterCandidates(searchResults);

    return (
        <div id="customer-report-panel">

          <div id="image-header">
              <div id="customer-logo"><img src={medLogo} alt="MED Institute Logo" /></div>
              <div className="report-button-bar">
                  <button
                      type="button"
                      title="Email Report"
                      onClick={handleEmail}
                      className="p-2 rounded hover:bg-gray-100"
                  >
                      {/* mail icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16v16H4z" />
                          <polyline points="22,6 12,13 2,6" />
                      </svg>
                  </button>
                  <button
                      type="button"
                      title="Print Report"
                      onClick={handlePrint}
                      className="p-2 rounded hover:bg-gray-100"
                  >
                      {/* printer icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 6 2 18 2 18 9" />
                          <path d="M6 18H4a2 2 0 0 1-2-2V9h20v7a2 2 0 0 1-2 2h-2" />
                          <rect x="6" y="14" width="12" height="8" />
                      </svg>
                  </button>
              </div>
          </div>

          <div id="report-header">
              <h1 className="report-title">Regulatory Assessment for Oxygenator Device</h1>
              <div className="report-subtitle">
                  <p>Prepared for: Medical Device Company</p>
                  <p>Prepared by: MED Institute</p>
                  <p>Date: {new Date().toLocaleDateString()}</p>
              </div>
          </div>

          {/* Executive Summary Section */}
          <div className="report-section">
            <div className="report-section-header">Executive Summary</div>
            <div className="report-section-content">
            <p className="text-sm leading-relaxed text-foreground/90">
              The client’s oxygenator device is a{" "}
              <b>single-use, hollow-fiber membrane oxygenator with integrated arterial filter and heat exchanger</b>,
              intended for short-term cardiopulmonary bypass procedures (up to 6 hours). It is a Class II medical device
              (21 CFR 870.4350, product code <b>DTZ</b>) under FDA regulation[1][2]. The device’s <b>intended use</b>{" "}
              matches that of the predicate Affinity Fusion Oxygenator (FDA 510(k) K122827): it oxygenates blood,
              removes carbon dioxide, and controls blood temperature via an integrated heat exchanger during
              extracorporeal circulation for routine cardiac surgery procedures up to 6 hours[3]. Our regulatory
              comparison indicates that the subject device has{" "}
              <b>equivalent intended use and technological characteristics</b> to the predicate device, supporting a
              determination of <b>substantial equivalence</b>. Key features – including a microporous hollow-fiber
              membrane, integrated <b>25 µm arterial filter</b>, integrated heat exchanger, and a biocompatible surface
              coating (Balance™ Biosurface) – are the <b>same as or very similar to the predicate</b>[4][5].
              <br />
              <br />
              The predicate device (Medtronic Affinity Fusion Oxygenator, K122827) was cleared by FDA in 2012 and
              remains a state-of-the-art oxygenator with <b>comparable design and performance</b>. By leveraging this
              predicate, the client can pursue a <b>Traditional 510(k)</b> submission demonstrating that any minor
              differences do not raise new questions of safety or effectiveness. Bench performance testing per FDA’s
              guidance on oxygenator 510(k) submissions[6][7] will be used to show the subject device meets or exceeds
              the same performance criteria as the predicate. Additional data (biocompatibility, sterilization
              validation, labeling, risk analysis) will be provided to fulfill all regulatory requirements.
              <br />
              <br />
              Overall, we find that the <b>subject oxygenator is likely substantially equivalent</b> to the chosen
              predicate. We recommend proceeding with a Traditional 510(k) submission, including all necessary
              supporting data outlined in this report. A summary of our findings and recommendations is presented below,
              along with a predicate comparison table, justification for predicate selection, required additional
              testing, a submission plan and timeline, cost estimates, and key assumptions. With proper preparation and
              adherence to FDA guidelines, we anticipate a smooth 510(k) review process leading to clearance of the
              device as a safe and effective oxygenator for cardiopulmonary bypass use.
            </p>

            </div>
          </div>

          {/* Predicate Device Comparison Section */}
          <div className="report-section">
            <div className="report-section-header">Predicate Device Comparison</div>
            <div className="report-section-content">
            <div className="border rounded-lg overflow-hidden">
              <table border={1}>
                <thead>
                  <tr>
                    <th className="font-bold" width="200">Characteristic</th>
                    <th className="font-bold" width="200">Your Device</th>
                    <th className="font-bold" width="200">Predicate Device</th>
                  </tr>
                </thead>
                <tablebody>
                </tablebody>
              </table>
            </div>

            </div>
          </div>

          {/* Next Steps Section */}
          <div className="report-section">
            <div className="report-section-header">Recommended Next Steps</div>
            <div className="report-section-content">
              <p className="text-sm leading-relaxed text-foreground/90">
                To advance your 510(k) submission, we recommend the following actions: (1) Complete comprehensive
                performance testing to demonstrate equivalent functionality to the predicate device, with particular focus
                on the LED light source validation; (2) Prepare detailed technical documentation including design
                specifications, risk analysis, and biocompatibility testing results; (3) Develop a thorough comparison
                table highlighting both similarities and differences with the predicate device, addressing any
                technological variations; (4) Engage with regulatory consultants to review the submission strategy and
                ensure all FDA requirements are met. We estimate the preparation timeline at 8-12 weeks, with an
                additional 3-6 months for FDA review following submission.
              </p>
            </div>
          </div>

          <div id="report-footer">
            <div className="footer-content">
              <p>This report is confidential and intended solely for the use of the recipient.</p>
              <p>© {new Date().getFullYear()} MED Institute Inc. All rights reserved.</p>
            </div>
          </div>

          <div className="mt-4">
              <button onClick={() => setShowCustomerReport(false)} className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300">
                  Close Report
              </button>
          </div>
      </div>
    );
};

export default CustomerReport;
